import React from 'react';
import {
  Show,
  ShowProps,
  useShowController,
  SimpleShowLayout,
  FunctionField,
  TextField,
  FileField,
  useGetIdentity,
  useTranslate,
} from 'react-admin';

import { Divider } from '@material-ui/core';
import HealthRiskCheckConfigurator from '../../../components/HealthRiskCheckConfigurator';
import SectionTitle from '../../../components/SectionTitle';
import TestHistory from '../../../components/TestHistory';
import TestResults from '../../../components/TestResults';
import { TestNames } from '../../../constants/test/testNames';
import { PartnerNames } from '../../../constants/partner/partnerNames';

import './styles.scss';

const calculateAge = (dateOfBirth: string) => {
  const date = new Date(dateOfBirth);
  const diff = Date.now() - date.getTime();
  const ageDate = new Date(diff);
  return Math.abs(ageDate.getUTCFullYear() - 1970);
};

interface CardiovascularAndDiabetesRiskCheckShowProps extends ShowProps {
  isReadOnly?: boolean;
}

const LANGUAGE_MAP: { [key: string]: string } = {
  en: 'English',
  de: 'German',
  fr: 'French',
  it: 'Italian',
};

const CardiovascularAndDiabetesRiskCheckShow = (
  props: CardiovascularAndDiabetesRiskCheckShowProps,
) => {
  const { isReadOnly } = props;
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const { record, error, loading } = useShowController(props);
  const isAdmin = identity?.userRoles.includes('admin');

  if (loading) {
    return <div>Loading</div>;
  }

  if (error) {
    return <div>Error {error}</div>;
  }

  return (
    <Show {...props} className="testShow">
      <SimpleShowLayout>
        <SectionTitle title={translate('testResults.userData')} />
        <TextField
          className="weightLossTreatmentCheckUserInfo text"
          source="user.fullName"
          label="testResults.fullName"
          emptyText="-"
        />

        <TextField
          className="weightLossTreatmentCheckUserInfo text"
          source="user.formattedDateOfBirth"
          label="testResults.dateOfBirth"
          emptyText="-"
        />
        <FunctionField
          className="weightLossTreatmentCheckUserInfo text"
          render={() => calculateAge(record?.user.dateOfBirth)}
          label="testResults.age"
        />

        <FunctionField
          className="weightLossTreatmentCheckUserInfo text"
          render={() => LANGUAGE_MAP[record?.user.language] || '-'}
          label="testResults.userLanguage"
        />

        <TextField
          className="weightLossTreatmentCheckUserInfo text"
          source="user.phoneNumber"
          label="testResults.phoneNumber"
          emptyText="-"
        />

        <TextField
          className="weightLossTreatmentCheckUserInfo text"
          source="user.emailAddress"
          label="testResults.email"
          emptyText="-"
        />

        <TextField
          className="weightLossTreatmentCheckUserInfo text"
          source="user.gender"
          label="testResults.gender"
          emptyText="-"
        />

        {isAdmin && record?.partnerCode && (
          <FunctionField
            className="weightLossTreatmentCheckUserInfo text"
            render={() =>
              PartnerNames[record?.partnerCode as keyof typeof PartnerNames]
            }
            label="testResults.partner"
            emptyText="-"
          />
        )}
        <Divider className="divider" />
        <TextField
          className="weightLossTreatmentCheckUserInfo text"
          source="answers.smoking"
          label="testResults.smoking"
          emptyText="-"
        />

        <TextField
          className="weightLossTreatmentCheckUserInfo text"
          source="answers.bloodPressureMeasuredValue"
          label="testResults.previouslyMeasuredBloodPressure"
          emptyText="-"
        />

        <TextField
          className="weightLossTreatmentCheckUserInfo text"
          source="answers.heartAttackInFamily"
          label="testResults.heartAttackInFamily"
          emptyText="-"
        />

        <TextField
          className="weightLossTreatmentCheckUserInfo text"
          source="answers.takingMedicationRegularly"
          label="testResults.takingMedicationRegularly"
          emptyText="-"
        />

        <TextField
          className="weightLossTreatmentCheckUserInfo text"
          source="answers.medicationName"
          label="testResults.medicationName"
          emptyText="-"
        />

        <Divider className="divider" />
        <TestHistory record={record} />
        <FileField
          target="_blank"
          className="text--purple"
          source="labReport"
          title={translate('testResults.openLabReport')}
          label="testResults.labReport"
        />
        <FunctionField
          className="text"
          render={() => TestNames[record?.type as keyof typeof TestNames]}
          label="testResults.testType"
        />
        {record && (
          <TestResults
            data={record.results.tests}
            partnerCode={record.partnerCode}
            userInfo={record.user}
          />
        )}
        <Divider className="divider" />
        <HealthRiskCheckConfigurator
          record={record}
          currentResource="tests/healthRiskCheck"
          readOnly={isReadOnly}
        />
      </SimpleShowLayout>
    </Show>
  );
};

const CardiovascularAndDiabetesRiskCheckShowReadOnly = (
  props: CardiovascularAndDiabetesRiskCheckShowProps,
) => <CardiovascularAndDiabetesRiskCheckShow {...props} isReadOnly />;

export {
  CardiovascularAndDiabetesRiskCheckShow,
  CardiovascularAndDiabetesRiskCheckShowReadOnly,
};
